import React, { createContext, useState, useContext, useEffect } from 'react';
import { applyMode, Mode } from '@cloudscape-design/global-styles';

// localStorage keys
const COLOR_THEME_KEY = 'documind-color-theme';
const VISUAL_MODE_KEY = 'documind-visual-mode';
const TOUCH_MODE_KEY = 'documind-touch-mode';

// Valid values
const VALID_COLOR_THEMES = ['default', 'summit'];
const VALID_VISUAL_MODES = ['light', 'dark'];
const VALID_TOUCH_MODES = ['off', 'on'];

// --- Pure helper functions (exported for testability / PBT) ---

/**
 * Read and validate colorTheme from localStorage.
 * Returns "default" if missing, invalid, or on error.
 */
export function readColorTheme() {
  try {
    const stored = localStorage.getItem(COLOR_THEME_KEY);
    if (stored && VALID_COLOR_THEMES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable — use default
  }
  return 'default';
}

/**
 * Read and validate visualMode from localStorage.
 * Returns "light" if missing, invalid, or on error.
 */
export function readVisualMode() {
  try {
    const stored = localStorage.getItem(VISUAL_MODE_KEY);
    if (stored && VALID_VISUAL_MODES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable — use default
  }
  return 'light';
}

/**
 * Read and validate touchMode from localStorage.
 * Returns "off" if missing, invalid, or on error.
 */
export function readTouchMode() {
  try {
    const stored = localStorage.getItem(TOUCH_MODE_KEY);
    if (stored && VALID_TOUCH_MODES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable — use default
  }
  return 'off';
}

/**
 * Return the opposite colorTheme value.
 */
export function nextColorTheme(current) {
  return current === 'default' ? 'summit' : 'default';
}

/**
 * Return the opposite visualMode value.
 */
export function nextVisualMode(current) {
  return current === 'light' ? 'dark' : 'light';
}

/**
 * Return the opposite touchMode value.
 */
export function nextTouchMode(current) {
  return current === 'off' ? 'on' : 'off';
}

// --- React Context ---

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [colorTheme, setColorTheme] = useState(readColorTheme);
  const [visualMode, setVisualMode] = useState(readVisualMode);
  const [touchMode, setTouchMode] = useState(readTouchMode);

  // Apply side-effects on mount and when state changes
  useEffect(() => {
    // Add / remove .summit-theme class on body
    if (colorTheme === 'summit') {
      document.body.classList.add('summit-theme');
    } else {
      document.body.classList.remove('summit-theme');
    }
  }, [colorTheme]);

  useEffect(() => {
    try {
      applyMode(visualMode === 'dark' ? Mode.Dark : Mode.Light);
    } catch (err) {
      console.error('Failed to apply visual mode:', err);
    }
  }, [visualMode]);

  useEffect(() => {
    // Add / remove .touch-mode class on body
    if (touchMode === 'on') {
      document.body.classList.add('touch-mode');
    } else {
      document.body.classList.remove('touch-mode');
    }
  }, [touchMode]);

  const toggleColorTheme = () => {
    setColorTheme((prev) => {
      const next = nextColorTheme(prev);
      try {
        localStorage.setItem(COLOR_THEME_KEY, next);
      } catch {
        // write failure — silently ignore
      }
      return next;
    });
  };

  const toggleVisualMode = () => {
    setVisualMode((prev) => {
      const next = nextVisualMode(prev);
      try {
        localStorage.setItem(VISUAL_MODE_KEY, next);
      } catch {
        // write failure — silently ignore
      }
      return next;
    });
  };

  const toggleTouchMode = () => {
    setTouchMode((prev) => {
      const next = nextTouchMode(prev);
      try {
        localStorage.setItem(TOUCH_MODE_KEY, next);
      } catch {
        // write failure — silently ignore
      }
      return next;
    });
  };

  const value = {
    colorTheme,
    visualMode,
    touchMode,
    toggleColorTheme,
    toggleVisualMode,
    toggleTouchMode,
    isSummit: colorTheme === 'summit',
    isDark: visualMode === 'dark',
    isTouch: touchMode === 'on',
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
